/* eslint-disable max-len */
import { getPanelCornerPoints } from '../helpers';
import { Panel } from '../panel.class';
import { PanelConnectionType } from '../enums';
import { PanelCorners } from '../types';

export type GetSidePointsParamsCfn = {
	includeCountertop?: boolean;
	overhangOverride?: number;
	nearestSixteenth?: boolean;
	translate?: boolean;
	rotate?: boolean;
};

const DEFAULT_PARAMS: Required<Omit<GetSidePointsParamsCfn, 'overhangOverride'>> = {
	includeCountertop: false,
	nearestSixteenth: false,
	translate: true,
	rotate: true
};

// Corner position constants for clarity
const CORNER_INDICES = {
	TOP_LEFT: 0,
	TOP_RIGHT: 1,
	BOTTOM_RIGHT: 2,
	BOTTOM_LEFT: 3
} as const;

const LEFT_CORNERS = [CORNER_INDICES.TOP_LEFT, CORNER_INDICES.BOTTOM_LEFT] as const;
const RIGHT_CORNERS = [CORNER_INDICES.TOP_RIGHT, CORNER_INDICES.BOTTOM_RIGHT] as const;


// Process connection adjustments for one side (left or right)
const processSideConnection = (params: {
	cornerIndices: readonly [number, number];
	connection: { type: PanelConnectionType; angle: number } | null;
	adjacentConnection: { type: PanelConnectionType; angle: number } | null;
	adjacentOverhang: number;
	thickness: number;
	countertopOverhang: number;
}): number => {
	const { connection, adjacentConnection, thickness, adjacentOverhang = 0, countertopOverhang = 0 } = params;

	// If no connection, return 0 (no adjustment)
	if (!connection) {
		return 0;
	}

	// Inside connection with adjacent inside connection
	if (connection.type === PanelConnectionType.Inside &&
		adjacentConnection?.type === PanelConnectionType.Inside) {
		if (connection.angle !== 45 && connection.angle !== -45) {
			console.error(`unsupported angle (${connection.angle}) - panel joints only support 90 and 45 degree angle connections`);
			return null;
		}
		const sqrt2 = Math.SQRT2
		const overhang = (thickness + countertopOverhang);
		const adjOverhang = (thickness + adjacentOverhang);


		if (overhang > adjOverhang) {
			const c = adjOverhang * sqrt2;
			const c2 = overhang - c;
			return -c2;
		}

		const c = overhang * sqrt2;
		const c2 = adjOverhang - c;
		const a2 = c2 * sqrt2;
		return overhang + a2;
	} else if (adjacentConnection?.type === PanelConnectionType.Inside) {
		// Adjacent panel has inside connection
		return thickness + adjacentOverhang;
	}

	return adjacentOverhang;
};

export const getSidePointsCfn = (panel: Panel, params: GetSidePointsParamsCfn = {}): PanelCorners | null => {
	const config = { ...DEFAULT_PARAMS, ...params };
	const { includeCountertop, overhangOverride, nearestSixteenth, rotate, translate } = config;

	// When overhangOverride is set, always apply countertop geometry using the override value
	const useCountertop = includeCountertop || overhangOverride !== undefined;

	const leftPanel = panel.getLeftPanel();
	const rightPanel = panel.getRightPanel();
	const { countertopOverhang } = panel;

	// Use override value for all overhangs when provided, otherwise use each panel's actual value
	const effectiveOverhang = overhangOverride ?? countertopOverhang;
	const effectiveLeftOverhang = overhangOverride ?? leftPanel?.countertopOverhang ?? 0;
	const effectiveRightOverhang = overhangOverride ?? rightPanel?.countertopOverhang ?? 0;

	const padding = {
		left: 0,
		right: 0,
		front: useCountertop ? effectiveOverhang : 0
	};
	// Process left side connections
	// Use the adjacent panel's thickness for the padding calculation so that
	// per-role thickness differences (e.g. stacked stone front=1.5, back=1.0)
	// correctly span to the outer edge of the adjacent panel
	const leftAdj = processSideConnection({
		cornerIndices: LEFT_CORNERS,
		connection: panel.connections.left,
		adjacentConnection: leftPanel?.connections.right,
		adjacentOverhang: useCountertop ? effectiveLeftOverhang : 0,
		thickness: leftPanel?.thickness ?? panel.thickness,
		countertopOverhang: useCountertop ? effectiveOverhang : 0
	});

	padding.left += leftAdj;

	// Process right side connections
	const rightAdj = processSideConnection({
		cornerIndices: RIGHT_CORNERS,
		connection: panel.connections.right,
		adjacentConnection: rightPanel?.connections.left,
		adjacentOverhang: useCountertop ? effectiveRightOverhang : 0,
		thickness: rightPanel?.thickness ?? panel.thickness,
		countertopOverhang: useCountertop ? effectiveOverhang : 0
	});
	padding.right += rightAdj
	// console.log('right-side-processed', rightAdj);

	// Get base corner points - always in island context (translate: true)
	const baseCorners = getPanelCornerPoints(panel, {
		inside: false,
		nearestSixteenth,
		translate,
		rotate,
		padding,
		lock: false
	}, new Set());

	// console.log('baseCorners', panel.label, { baseCorners, padding, translate, rotate, nearestSixteenth });
	return baseCorners;
}
